/**
 * @file Settings page.
 * @description Manages system configuration, export settings, activity history, and session logout actions.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Settings as SettingsIcon, Save, Calendar, Database, FileText, LogOut } from 'lucide-react';
import { PageHeader } from '../components/ui';
import { SettingsService, HistoryItem, type TimeZoneOption } from '../services/settingsService';
import { ExportSettingsService } from '../services/exportSettingsService';
import { apiFetch } from '../api/api';
import { AuthService } from '../services/authService';

const DEFAULT_FORM_DATA = {
  export_log_days: 7,
  duplicate_check: 30,
  max_file_size: 10,
  time_zone: 'Asia/Kolkata',
  auto_validation: false,
  email_notification: false,
};

type SettingsUpdateResponse = {
  status: string;
  message: string;
};

const Settings: React.FC = () => {
  // Logout handler
  const handleLogout = () => {
    AuthService.logout();
  };
  const [uploadHistory, setUploadHistory] = useState<HistoryItem[]>([]);
  const [exportHistory, setExportHistory] = useState<HistoryItem[]>([]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [notification, setNotification] = useState<{message: string, type: 'error' | 'success'} | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lockDurationOptions] = useState(ExportSettingsService.getLockDurationOptions());
  const [timeZoneOptions, setTimeZoneOptions] = useState<TimeZoneOption[]>([
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata' },
  ]);
  const [isLoadingTimeZones, setIsLoadingTimeZones] = useState(false);
  const notificationTimeoutRef = useRef<number | null>(null);
  
  // Form state for settings
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const showNotification = useCallback((message: string, type: 'success' | 'error' = 'error') => {
    if (notificationTimeoutRef.current) {
      window.clearTimeout(notificationTimeoutRef.current);
    }
    setNotification({ message, type });
    notificationTimeoutRef.current = window.setTimeout(() => setNotification(null), 5000);
  }, []);

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        window.clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // Fetch current settings from API
  const fetchCurrentSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    setIsLoadingTimeZones(true);
    try {
      const [settingsResponse, timeZoneSettings] = await Promise.all([
        SettingsService.getSettings(),
        SettingsService.getTimeZoneSettings().catch(() => null),
      ]);
      if (timeZoneSettings) {
        setTimeZoneOptions(timeZoneSettings.options);
      }
      const settings = settingsResponse.data?.settings;
      if (settings) {
        const timeZone = settings.time_zone || timeZoneSettings?.selected || 'Asia/Kolkata';
        setFormData({
          export_log_days: Number.parseInt(settings.export_log_days, 10) || 7,
          duplicate_check: Number.parseInt(settings.duplicate_check, 10) || 30,
          max_file_size: Number.parseInt(settings.max_file_size, 10) || 10,
          time_zone: timeZone,
          auto_validation: settings.auto_validation === '1',
          email_notification: settings.email_notification === '1',
        });
        if (timeZoneSettings) {
          setTimeZoneOptions(SettingsService.ensureTimeZoneInOptions(timeZoneSettings.options, timeZone));
        }
      }
    } catch {
      showNotification('Failed to load settings', 'error');
    } finally {
      setIsLoadingSettings(false);
      setIsLoadingTimeZones(false);
    }
  }, [showNotification]);

  // Fetch settings and history from the backend
  useEffect(() => {
    const fetchData = async () => {
      setIsPageLoading(true);
      setIsLoadingTimeZones(true);
      try {
        const [settingsResponse, exportDaysResponse, timeZoneSettings] = await Promise.all([
          SettingsService.getSettings(),
          ExportSettingsService.getExportLogDays(),
          SettingsService.getTimeZoneSettings().catch(() => null),
        ]);

        if (timeZoneSettings) {
          setTimeZoneOptions(timeZoneSettings.options);
        }

        const settings = settingsResponse.data?.settings;
        if (settings) {
          const timeZone = settings.time_zone || timeZoneSettings?.selected || 'Asia/Kolkata';
          setFormData({
            export_log_days: Number.parseInt(settings.export_log_days, 10) || 7,
            duplicate_check: Number.parseInt(settings.duplicate_check, 10) || 30,
            max_file_size: Number.parseInt(settings.max_file_size, 10) || 10,
            time_zone: timeZone,
            auto_validation: settings.auto_validation === '1',
            email_notification: settings.email_notification === '1',
          });
          if (timeZoneSettings) {
            setTimeZoneOptions(SettingsService.ensureTimeZoneInOptions(timeZoneSettings.options, timeZone));
          }
        } else {
          setFormData(DEFAULT_FORM_DATA);
        }

        if (exportDaysResponse?.data?.export_log_days) {
          setFormData(prev => ({ ...prev, export_log_days: exportDaysResponse.data.export_log_days }));
        }

        setUploadHistory(Array.isArray(settingsResponse.data?.recentUploads) ? settingsResponse.data.recentUploads as HistoryItem[] : []);
        setExportHistory(Array.isArray(settingsResponse.data?.recentExports) ? settingsResponse.data.recentExports as HistoryItem[] : []);

        if (settingsResponse.status === 'error') {
          showNotification(settingsResponse.message, 'error');
        }
      } catch {
        showNotification('Failed to load settings. Using default values.', 'error');
        setFormData(DEFAULT_FORM_DATA);
        setUploadHistory([]);
        setExportHistory([]);
      } finally {
        setIsPageLoading(false);
        setIsLoadingTimeZones(false);
      }
    };
    fetchData();
  }, [showNotification]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Create FormData as per API requirements
      const formDataToSend = new FormData();
      formDataToSend.append('export_log_days', formData.export_log_days.toString());
      formDataToSend.append('duplicate_check', formData.duplicate_check.toString());
      formDataToSend.append('max_file_size', formData.max_file_size.toString());
      formDataToSend.append('time_zone', formData.time_zone);
      formDataToSend.append('auto_validation', formData.auto_validation ? '1' : '0');
      formDataToSend.append('email_notification', formData.email_notification ? '1' : '0');

      const result = await apiFetch<SettingsUpdateResponse>('settings', 'POST', formDataToSend);
      showNotification(result.message || 'Settings saved successfully', 'success');
    } catch {
      showNotification('Failed to save settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const clearHistory = async (type: 'upload' | 'export') => {
    // eslint-disable-next-line no-alert
    if (confirm(`Are you sure you want to clear ${type} history?`)) {
        try {
            const result = await SettingsService.clearHistory(type);
            showNotification(result.message || `${type} history cleared successfully`, 'success');
            // Refresh the history list on the frontend
            if (type === 'upload') {
                setUploadHistory([]);
            } else {
                setExportHistory([]);
            }
        } catch {
            showNotification(`Failed to clear ${type} history`, 'error');
        }
    }
  };

  const formatDate = (dateString: string) => {
    return SettingsService.formatDate(dateString);
  };

  const recentUploads = useMemo(() => uploadHistory.slice(0, 5), [uploadHistory]);
  const recentExports = useMemo(() => exportHistory.slice(0, 5), [exportHistory]);


  return (
    <div className="space-y-6">
      {isPageLoading && (
        <div className="bg-white rounded-lg shadow-lg p-4 text-sm text-gray-600">
          Loading settings...
        </div>
      )}
      {/* Notification */}
      {notification && (
        <div className={`p-4 rounded-md text-white ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {notification.message}
        </div>
      )}
      
      <PageHeader
        title="Settings"
        subtitle="Configure system settings and view activity logs"
        gradientClassName="from-red-600 to-red-800"
        subtitleClassName="text-red-100 text-sm"
        actions={
          <button
            onClick={handleLogout}
            title="Logout"
            className="flex items-center gap-2 px-6 cursor-pointer py-3 bg-white text-red-700 rounded-lg font-semibold transition text-sm hover:bg-red-50"
          >
            <LogOut size={22} className="mr-1" /> Logout
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Settings - UNCHANGED */}
        <div className="bg-white rounded-lg p-4 md:p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-5 pb-2 border-b border-gray-200 border-b-1 flex items-center">
            <SettingsIcon size={20} className="mr-2" />
            System Configuration
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* <div> */}
              {/* <label className="block text-sm font-medium text-gray-700 mb-1">Export Lock Duration (Days)</label> */}
              {/* <select
                value={formData.export_log_days}
                onChange={(e) => setFormData({ ...formData, export_log_days: Number.parseInt(e.target.value, 10) })}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md"
              >
                {lockDurationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
              {/* <p className="text-sm text-gray-500 mt-1">Prevent re-export of same campaign data within this period</p> */}
            {/* </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duplicate Check Period (Days)</label>
              <select
                value={formData.duplicate_check}
                onChange={(e) => setFormData({ ...formData, duplicate_check: Number.parseInt(e.target.value, 10) })}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value={7}>7 days</option>
                <option value={15}>15 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
              {/* <p className="text-sm text-gray-500 mt-1">Check for duplicates within this time frame</p> */}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Maximum File Size (MB)</label>
              <select
                value={formData.max_file_size}
                onChange={(e) => setFormData({ ...formData, max_file_size: Number.parseInt(e.target.value, 10) })}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value={5}>5 MB</option>
                <option value={10}>10 MB</option>
                <option value={25}>25 MB</option>
                <option value={50}>50 MB</option>
                <option value={100}>100 MB</option>
                <option value={1000}>1000 MB</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Zone</label>
              <select
                value={formData.time_zone}
                onChange={(e) => setFormData({ ...formData, time_zone: e.target.value })}
                disabled={isLoadingTimeZones}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {timeZoneOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-full">
              <div className="flex items-center justify-between space-y-1">
                <label className="text-sm font-medium text-gray-700">Auto Validation</label>
                <button
                  onClick={() => setFormData({ ...formData, auto_validation: !formData.auto_validation })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.auto_validation ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.auto_validation ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-500">Automatically validate uploaded data</p>
            </div>

            <div className="col-span-full">
              <div className="flex items-center justify-between space-y-1">
                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                <button
                  onClick={() => setFormData({ ...formData, email_notification: !formData.email_notification })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.email_notification ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.email_notification ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-sm text-gray-500">Receive email notifications for uploads and exports</p>
            </div>

            <div className="col-span-full mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={fetchCurrentSettings}
                disabled={isLoadingSettings}
                className={`flex-1 px-5 py-3 text-sm font-medium cursor-pointer rounded-md transition-colors ${
                  isLoadingSettings 
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed' 
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {isLoadingSettings ? 'Loading...' : 'Refresh Settings'}
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className={`flex-1 px-4 py-2 text-sm font-medium cursor-pointer rounded-md transition-colors ${
                  isSaving
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-white bg-gradient-to-r from-primary-500 to-secondary-500'
                }`}
              >
                <Save size={16} className="inline mr-2" />
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>

        {/* Activity Monitor - UNCHANGED */}
        <div className="bg-white rounded-lg p-4 md:p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-5 pb-2 border-b border-gray-200 border-b-1 flex items-center">
            <Database size={20} className="mr-2" />
            System Status
          </h2>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm font-medium text-green-800">System Online</span>
              </div>
              <p className="text-sm text-green-600 mt-1">All services are operational</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="text-center px-3 py-5 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{uploadHistory?.length || 0}</p>
                <p className="text-sm text-gray-600">Total Uploads</p>
              </div>
              <div className="text-center px-3 py-5 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{exportHistory?.length || 0}</p>
                <p className="text-sm text-gray-600">Total Exports</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => clearHistory('upload')} className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
                Clear Upload History
              </button>
              <button onClick={() => clearHistory('export')} className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
                Clear Export History
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Logs - UNCHANGED */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-4 md:p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText size={20} className="mr-2" />
            Recent Uploads
          </h3>
          <div className="space-y-3">
            {recentUploads.map((upload) => (
              <div key={upload.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start flex-wrap">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{upload['filename']}</p>
                    <p className="text-xs text-gray-600">{upload['advertiser_name']} - {upload['campaign_name']}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-green-600">{upload['valid_records']} valid</p>
                    <p className="text-xs text-red-600">{upload['duplicate_records']} duplicates</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{formatDate(upload['uploaded_at'] || '')}</p>
              </div>
            ))}
            {(uploadHistory?.length || 0) === 0 && <p className="text-sm text-gray-500 text-center py-4">No upload history available</p>}
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 md:p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Calendar size={20} className="mr-2" />
            Recent Exports
          </h3>
          <div className="space-y-3">
            {recentExports.map((exportItem) => (
              <div key={exportItem.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start flex-wrap">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Campaign ID: {exportItem['campaign_id']}</p>
                    <p className="text-xs text-gray-600 break-all">File: {exportItem['filename']}</p>
                    <p className="text-xs text-gray-600">Locked for {exportItem['lock_duration_days']} days</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-2">
                      <p className="text-xs text-blue-600 mb-0">Exported</p>
                      <button
                        className="px-2 py-1 text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded hover:scale-105 transition"
                        onClick={async () => {
                          try {
                            const blob = await SettingsService.reExportFile(
                              exportItem['campaign_id'] || '',
                              exportItem['filename'] || 'export.csv',
                            );
                            SettingsService.downloadFile(blob, exportItem['filename'] || 'export.csv');
                          } catch {
                            // Handle download error silently
                          }
                        }}
                      >
                        Re-Export
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{formatDate(exportItem['exported_at'] || '')}</p>
              </div>
            ))}
            {(exportHistory?.length || 0) === 0 && <p className="text-sm text-gray-500 text-center py-4">No export history available</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
